import { useRef } from "react";
import { motion, useMotionTemplate, useScroll, useTransform, useSpring } from "framer-motion";

const KEYHOLE_MASK =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 140'%3E%3Cdefs%3E%3Cfilter id='b' x='-40%25' y='-40%25' width='180%25' height='180%25'%3E%3CfeGaussianBlur stdDeviation='4'/%3E%3C/filter%3E%3C/defs%3E%3Cpath d='M50 8 A32 32 0 0 1 65 63 L79 132 L21 132 L35 63 A32 32 0 0 1 50 8 Z' fill='white' filter='url(%23b)'/%3E%3C/svg%3E\")";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const vh = typeof window !== "undefined" ? window.innerHeight : 900;
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const holeW = isMobile ? 0.3 : 0.393;
  const room0 = holeW * 1.4;
  const keyholeS = useTransform(scrollYProgress, [0, 0.45, 0.72], [1, 1 / room0, 14]);
  const rawMaskPx = useTransform(keyholeS, (s) => s * vh * holeW);
  const maskPx = useSpring(rawMaskPx, { stiffness: 55, damping: 20, mass: 0.6 });
  const maskSize = useMotionTemplate`${maskPx}px`;
  const rawRoomScale = useTransform(scrollYProgress, [0, 0.45], [room0, 1]);
  const roomScale = useSpring(rawRoomScale, { stiffness: 55, damping: 20, mass: 0.6 });

  const introOpacity = useTransform(scrollYProgress, [0.15, 0.4], [1, 0]);
  const introY = useTransform(scrollYProgress, [0.15, 0.4], [0, -60]);
  const revealOpacity = useTransform(scrollYProgress, [0.68, 0.9], [0, 1]);
  const revealY = useTransform(scrollYProgress, [0.68, 0.9], [40, 0]);
  const vignette = useTransform(scrollYProgress, [0, 0.72], [0, 0.32]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative h-[320vh]"
      data-testid="hero-section"
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-[#050706]">
        <motion.div
          style={{
            WebkitMaskImage: KEYHOLE_MASK,
            maskImage: KEYHOLE_MASK,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "50% 45%",
            maskPosition: "50% 45%",
            WebkitMaskSize: maskSize,
            maskSize,
          }}
          className="absolute inset-0 will-change-transform"
        >
          <motion.div
            style={{ scale: roomScale, transformOrigin: "50% 45%" }}
            className="absolute inset-0 will-change-transform"
          >
            <img
              src="/images/zenique-hero-room.jpg"
              alt="A dark, warm ZENIQUE library living room with a deep burgundy wall"
              className="h-full w-full object-cover"
              data-testid="hero-room-image"
            />
            <div className="sconce-glow" data-testid="hero-sconce-glow" />
          </motion.div>
        </motion.div>

        <motion.div
          style={{ opacity: vignette }}
          className="absolute inset-0 bg-noir pointer-events-none"
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(5,7,6,0.55)_100%)] pointer-events-none" />

        <motion.div
          style={{ opacity: introOpacity, y: introY }}
          className="absolute inset-0 px-6 md:px-12 lg:px-24 flex items-center"
        >
          <div className="grid grid-cols-12 w-full h-full md:h-auto content-between md:content-center md:items-center gap-y-10 py-28 md:py-0">
            <div className="col-span-12 md:col-span-5">
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 2.9, ease: [0.22, 1, 0.36, 1] }}
                className="overline-label mb-6"
                data-testid="hero-overline"
              >
                Lifestyle · Interior Styling
              </motion.p>
              <h1
                className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl tracking-[0.12em] text-cream flex overflow-hidden"
                data-testid="hero-title"
              >
                {["Z", "E", "N", "I", "Q", "U", "E"].map((letter, i) => (
                  <motion.span
                    key={i}
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 3.0 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {letter}
                  </motion.span>
                ))}
              </h1>
              <div className="overflow-hidden mt-6">
                <motion.p
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 3.7, ease: [0.22, 1, 0.36, 1] }}
                  className="max-w-md text-sm md:text-base font-light text-cream/70 leading-relaxed"
                  data-testid="hero-intro-copy"
                >
                  Scroll, and let the room reveal itself.
                </motion.p>
              </div>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 flex flex-col items-start gap-8">
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 3.3, ease: [0.22, 1, 0.36, 1] }}
                className="text-sm md:text-base font-light text-cream/70 leading-relaxed"
                data-testid="hero-side-copy"
              >
                A residential design and furnishing house — taking you from new
                property to a home that already feels like yours.
              </motion.p>
              <motion.a
                href="#contact"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 3.7 }}
                className="group inline-flex items-center gap-4 border border-gold/40 px-8 py-4 text-xs tracking-[0.3em] uppercase text-gold hover:bg-gold hover:text-noir transition-colors duration-500"
                data-testid="hero-cta-button"
              >
                Begin an Enquiry
                <span className="h-px w-8 bg-gold group-hover:bg-noir transition-colors duration-500" />
              </motion.a>
            </div>
          </div>
        </motion.div>

        <motion.div
          style={{ opacity: revealOpacity, y: revealY }}
          className="absolute inset-x-0 bottom-0 pb-24 md:pb-28 px-6 md:px-12 lg:px-24"
        >
          <h2
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-cream max-w-3xl leading-tight"
            data-testid="hero-tagline"
          >
            Make home your escape
          </h2>
        </motion.div>

        <motion.div
          style={{ opacity: introOpacity }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
          data-testid="hero-scroll-indicator"
        >
          <span className="text-[10px] tracking-[0.4em] uppercase text-taupe">Scroll</span>
          <span className="block h-14 w-px bg-gold/60 animate-scroll-line" />
        </motion.div>
      </div>
    </section>
  );
}
